import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { TagChip } from './GuestTagChips';
import { GUEST_SECONDARY_BUTTON_SX, GUEST_TEXT_BUTTON_SX } from './guestTableStyles';
import {
  TAG_KIND_LABELS,
  TAG_KINDS,
  asTagKind,
  isHealthKind,
  planCriticalChange,
  planTagKindChange,
  tagEditSendsNow,
  sortTags,
  tagCounts,
  tagColor,
  tagLabelProblem,
  type TagEditPlan,
} from '../../../lib/guestTags';
import type { GuestTag, TrackerGuest } from '../../../services/tracker-api-client';

export interface TagEdit {
  label?: string;
  kind?: string;
  color?: string | null;
  isCritical?: boolean;
}

/**
 * One tag's row.
 *
 * Its own component because three of its controls hold state that belongs to
 * this row and nothing else — a colour mid-drag, a rename that was refused, an
 * edit waiting on a confirmation. Keyed by tag id in the dialog above, all
 * three would have to be maps, and a map keyed by a list that re-sorts after
 * every write is how a confirmation ends up attached to the wrong tag.
 *
 * Nothing here composes a patch by hand. `planTagKindChange` and
 * `planCriticalChange` decide what goes on the wire and what must be agreed to
 * first, in a library with unit tests; this renders their answer.
 */
function TagRow({
  tag,
  others,
  used,
  busy,
  onEdit,
  onDelete,
}: {
  tag: GuestTag;
  /** Every OTHER tag, so a rename can be refused by name against the one it clashes with. */
  others: GuestTag[];
  used: number;
  busy: boolean;
  onEdit: (tagId: string, edit: TagEdit) => void;
  onDelete: (tagId: string, force: boolean) => void;
}) {
  /** Why the last rename was refused. '' when the field is fine. */
  const [renameProblem, setRenameProblem] = useState('');
  /** The colour being dragged, or null when the picker is showing the stored one. */
  const [colorDraft, setColorDraft] = useState<string | null>(null);
  /** An edit the host has to agree to before it is sent. */
  const [pending, setPending] = useState<TagEditPlan | null>(null);
  const [deleting, setDeleting] = useState(false);

  const kind = asTagKind(tag.kind);
  const critical = Boolean(tag.isCritical);

  /**
   * The one path an edit takes out of this row: an empty patch is not a
   * request, and a patch carrying a confirmation is not sent until the host
   * has read it.
   */
  const send = (plan: TagEditPlan) => {
    // Any new pick replaces the last one, including a pick back to the kind the
    // tag already has — otherwise the confirmation for an abandoned choice
    // stays on screen under a control that no longer shows it.
    setPending(plan.confirm ? plan : null);
    // The gate is tagEditSendsNow, in the library, so that defeating it fails a
    // test rather than passing 676 of them.
    if (!tagEditSendsNow(plan)) return;
    onEdit(tag.id, plan.patch);
  };

  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid var(--tsb-border-soft)',
        borderRadius: 'var(--tsb-radius-md)',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
        <TextField
          size="small"
          label="Name"
          defaultValue={tag.label}
          disabled={busy}
          error={Boolean(renameProblem)}
          helperText={renameProblem}
          onChange={() => setRenameProblem('')}
          onBlur={(event) => {
            const next = event.target.value.trim();
            if (next === tag.label) {
              setRenameProblem('');
              return;
            }
            /*
              Refused in the browser rather than at the server so the host is
              told which tag they already have, by name — and told AT THE FIELD.
              A rename refused in silence looks exactly like one that saved: the
              field still shows what was typed, and the old name is only
              discovered on the next open.
            */
            const problem = tagLabelProblem(next, others);
            setRenameProblem(problem);
            if (problem) return;
            onEdit(tag.id, { label: next });
          }}
          sx={{ flex: '1 1 180px' }}
        />
        <TextField
          select
          size="small"
          label="Kind"
          // The pending kind, so the select shows what the host picked while
          // they are reading what picking it costs.
          value={pending?.patch.kind ?? kind}
          disabled={busy}
          onChange={(event) => send(planTagKindChange(tag, event.target.value))}
          sx={{ minWidth: 160 }}
        >
          {TAG_KINDS.map((option) => (
            <MenuItem key={option} value={option}>
              {TAG_KIND_LABELS[option]}
            </MenuItem>
          ))}
        </TextField>
        <Box
          component="input"
          type="color"
          aria-label={`Colour for ${tag.label}`}
          /*
            Committed on blur, exactly like the Name field beside it. The native
            picker fires a change event for every movement inside it, and
            PATCHing each one takes a write ticket per twitch — while the input
            is controlled and disabled mid-write, so it snaps back under the
            host's cursor and what persists is whichever response lands last.
          */
          value={colorDraft ?? tagColor(tag)}
          disabled={busy}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setColorDraft(event.target.value)
          }
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            const next = event.target.value;
            setColorDraft(null);
            if (next.toLowerCase() === tagColor(tag)) return;
            onEdit(tag.id, { color: next });
          }}
          sx={{ width: 40, height: 34, border: 'none', bgcolor: 'transparent', p: 0, mt: 0.25 }}
        />
      </Stack>

      <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" sx={{ gap: 1, mt: 1 }}>
        <TagChip tag={tag} />
        <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12 }}>
          {`${tag.slug} · on ${used} ${used === 1 ? 'guest' : 'guests'}`}
          {isHealthKind(kind) ? ' · host only' : ''}
        </Typography>
        {/*
          The other half of the kind change: without a control that can set the
          flag, clearing it — however deliberately — is a one-way door. The
          server takes isCritical on the same PATCH, so this is the request it
          has been asking for all along.
        */}
        <Tooltip
          title={
            isHealthKind(kind)
              ? 'Critical tags carry the danger outline, sort first, and are what the kitchen sheet is made of.'
              : 'Only a food or access tag can go on the kitchen sheet.'
          }
        >
          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={critical}
                disabled={busy || !isHealthKind(kind)}
                onChange={(event) => send(planCriticalChange(tag, event.target.checked))}
                inputProps={{ 'aria-label': `On the kitchen sheet: ${tag.label}` }}
              />
            }
            label={
              <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12 }}>
                On the kitchen sheet
              </Typography>
            }
          />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        {deleting ? (
          <>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={busy}
              onClick={() => {
                onDelete(tag.id, used > 0);
                setDeleting(false);
              }}
              sx={GUEST_SECONDARY_BUTTON_SX}
            >
              {used > 0 ? `Delete it off ${used}` : 'Delete'}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setDeleting(false)}
              sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
            >
              Keep
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => setDeleting(true)}
            sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-danger-ink)' }}
          >
            Delete
          </Button>
        )}
      </Stack>

      {deleting && used > 0 ? (
        <Typography sx={{ mt: 0.75, color: 'var(--tsb-warning-ink)', fontSize: 12 }}>
          {`This takes “${tag.label}” off ${used} ${used === 1 ? 'guest' : 'guests'}. There is no undo.`}
        </Typography>
      ) : null}

      {/*
        The same two-step the delete beside it uses, for the same reason: what
        it costs is not visible on the control that causes it. Un-criticalling
        an allergy is the failure the server refuses the one-field version of,
        and a <select> that did it as a side effect would be that refusal
        defeated rather than answered.
      */}
      {pending ? (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ color: 'var(--tsb-warning-ink)', fontSize: 12, lineHeight: 1.55 }}>
            {pending.confirm}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={busy}
              onClick={() => {
                onEdit(tag.id, pending.patch);
                setPending(null);
              }}
              sx={GUEST_SECONDARY_BUTTON_SX}
            >
              Take it off the sheet
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setPending(null)}
              sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
            >
              Leave it as it is
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Rename, recolour, retype, mark critical, delete.
 *
 * This dialog is what makes free-text tags survivable. Without a rename the
 * first typo is permanent — "collge friends" sits on forty guests forever and
 * the host works around it by making a second tag. Everything else here is
 * convenience; the rename is the feature.
 *
 * Two edits ask twice, and they are the two that lose something: deleting a tag
 * off the guests carrying it, and taking one off the kitchen sheet. Both
 * confirmations carry the fact that makes them readable — the count, or the
 * name of the tag — because a confirmation that does not say twelve is a
 * confirmation nobody reads.
 */
export default function TagManagerDialog({
  tags,
  guests,
  busy,
  error,
  onEdit,
  onDelete,
  onClose,
}: {
  tags: GuestTag[];
  guests: TrackerGuest[];
  busy: boolean;
  error: string;
  onEdit: (tagId: string, edit: TagEdit) => void;
  /** `force` deletes it off the guests still carrying it. */
  onDelete: (tagId: string, force: boolean) => void;
  onClose: () => void;
}) {
  const counts = tagCounts(guests);
  const ordered = sortTags(tags);

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
      <DialogTitle sx={{ pb: 1 }}>Manage tags</DialogTitle>
      <DialogContent dividers>
        {ordered.length === 0 ? (
          <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12.5 }}>
            No tags on this trip yet.
          </Typography>
        ) : null}

        <Stack spacing={2}>
          {ordered.map((tag) => (
            <TagRow
              key={tag.id}
              tag={tag}
              others={tags.filter((other) => other.id !== tag.id)}
              used={counts[tag.id] ?? 0}
              busy={busy}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="text" disabled={busy} onClick={onClose} sx={GUEST_TEXT_BUTTON_SX}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
